import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { SuccessDialogState } from './enums';

interface TransactionSubmitSuccessDialogProps {
  /** Controls whether the dialog is open */
  state: SuccessDialogState;
  /** Callback when the "View Transaction" button is clicked */
  onViewTransaction: () => void;
}

/**
 * Success dialog shown after transaction has been successfully queued
 * Displays success message and a button to view the transaction
 */
export function TransactionSubmitSuccessDialog({
  state,
  onViewTransaction,
}: TransactionSubmitSuccessDialogProps): React.ReactElement {
  // The state tells us how many more approvals are needed - using convenience variables here to make it more clear
  const numApprovalsNeeded: number = state;
  const onlyOneMoreApprovalNeeded = numApprovalsNeeded === 1;

  return (
    <Dialog open={state !== SuccessDialogState.Closed}>
      <DialogContent
        className="w-[456px] rounded-[15px] border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 [&>button]:hidden p-0"
        onInteractOutside={(e) => {
          // Allow closing by clicking outside
          e.preventDefault();
        }}
      >
        <div className="flex flex-col items-center justify-center overflow-clip rounded-[inherit] w-full">
          {/* Header Section */}
          <div className="flex flex-col gap-[22px] items-center justify-center p-[20px] w-full">
            <DialogHeader className="items-center px-[12px]">
              <DialogTitle className="text-[16px] font-semibold text-zinc-600 dark:text-zinc-400 w-full whitespace-pre-wrap">
                Your transaction has been successfully queued!
              </DialogTitle>
            </DialogHeader>
          </div>

          {/* Description Section */}
          <div className="flex flex-col items-center justify-center px-[30px] w-[394px]">
            <DialogDescription className="text-[14px] text-zinc-500 dark:text-zinc-400 text-center w-full whitespace-pre-wrap leading-[20px]">
              {state === SuccessDialogState.ReadyToExecute
                ? 'This transaction can be executed at any time.'
                : `This transaction needs ${numApprovalsNeeded} more approval${onlyOneMoreApprovalNeeded ? '' : 's'} before it can be executed.`}
            </DialogDescription>
          </div>

          {/* Action Button Section */}
          <div className="flex flex-col gap-[10px] items-center justify-center pb-[20px] pt-[50px] w-full">
            <Button
              onClick={onViewTransaction}
              className="bg-zinc-900 dark:bg-zinc-100 hover:bg-zinc-800 dark:hover:bg-zinc-200 h-[37px] rounded-[9px] px-[19px] py-[6px] gap-[10px]"
            >
              <span className="text-[14px] font-semibold text-white dark:text-zinc-900">
                View Transaction
              </span>
              <ArrowRight className="size-[15px] text-white dark:text-zinc-900" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
